import * as THREE from "three";
import { Cell, CellKind, Grid, headingBetween, headingOffset } from "@highmarch/domain";
import { Terrain, roadLevelAt } from "../terrain";
import { cellToWorld } from "../scene";
import { HQ_PORTAL, HQ_YAW, lib, yawFor } from "./common";
import { OptionBadge, P2, lineMesh, marchingEdgeMaterial, optionBadge, roadEdges } from "./placementMarks";
import { badgeMaterial, deckHeights } from "./placementShapes";

/**
 * The road card's preview marks (placement.ts PlacementView.showGhost draws them): the other options' white routes,
 * the chosen option's marching edges, the portal's and the bridges' see-through ghosts, and the options' numbers.
 */

/** Where the marks go, and what they own: PlacementView's group, its dispose list and its ghost look. */
export interface PreviewHost {
  readonly group: THREE.Group;
  own<T extends { dispose(): void }>(x: T): T;
  ghostify(root: THREE.Object3D): void;
  /** False under Reduced effects: the edge holds still. */
  motion(): boolean;
}

/** Half the width between the two glowing edges (cells): just outside the painted road's kerb. */
export const EDGE_HALF_WIDTH = 0.47;
/** The glowing edge line, and the dark keyline under it that keeps it readable on pale ground. */
export const EDGE_WIDTH = 0.075;
export const EDGE_KEYLINE_WIDTH = 0.14;
let edgeMaterial: THREE.ShaderMaterial | null = null;
const edgeKeyline = new THREE.MeshBasicMaterial({ color: 0x1c1206, opacity: 1, transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false, side: THREE.DoubleSide });

/** Width of the other options' routes, in cells, and how much wider their dark keyline is. */
export const OPTION_WIDTH = 0.14;
export const OPTION_KEYLINE = 0.07;
/** Height of the band above the ground at each point, so it sits on the relief without z-fighting it. */
export const RIBBON_LIFT = 0.05;

// The route is OPAQUE: it is a union of overlapping discs and strips, and at partial opacity every overlap would
// blend twice and show as a dark blotch at the joints.
export const optionKeyline = new THREE.MeshBasicMaterial({ color: 0x1c1206, opacity: 1, transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false, side: THREE.DoubleSide });
export const optionFrame = new THREE.MeshBasicMaterial({ color: 0xe8efe4, opacity: 1, transparent: true, depthTest: false, depthWrite: false, fog: false, toneMapped: false, side: THREE.DoubleSide });

/**
 * The chosen option's outline: both edges of the road it will lay, as bright dashes marching from the open end to
 * the far end over a dark keyline. The painted preview alone looked like road already laid; the moving edge says
 * "not yet, and it goes this way" without drawing an arrow (the owner asked for the path, not an arrow).
 */
export function roadEdge(host: PreviewHost, from: Cell | null, cells: readonly Cell[], grid: Grid | null): void {
  // start half a cell out of the open end, where the new road meets the old
  const path: P2[] = from && cells.length > 0 ? [{ x: (from.x + cells[0].x) / 2, y: (from.y + cells[0].y) / 2 }, ...cells] : [...cells];
  if (path.length < 2) return;
  const { left, right } = roadEdges(path, EDGE_HALF_WIDTH);
  const deck = deckHeights(from ? [from, ...cells] : cells, grid);
  // Over a chasm the edge runs at the bridge's deck height (the bank it leaves from), not down on the chasm floor:
  // drawn without a depth test, a floor-height edge dropped the outline into the gorge as a zig-zag.
  const h = (x: number, z: number) => {
    let best = cells[0], bd = Infinity;
    for (const c of cells) { const d = (c.x - x) ** 2 + (c.y - z) ** 2; if (d < bd) { bd = d; best = c; } }
    return (deck.get(`${best.x},${best.y}`) ?? roadLevelAt(x, z)) + RIBBON_LIFT;
  };
  const edge = edgeMaterial ??= marchingEdgeMaterial();
  for (const side of [left, right]) {
    const keyMesh = lineMesh(side, EDGE_KEYLINE_WIDTH, h, edgeKeyline);
    host.own(keyMesh.geometry);
    const line = lineMesh(side, EDGE_WIDTH, h, edge);
    host.own(line.geometry);
    keyMesh.renderOrder = 6; line.renderOrder = 7;
    line.onBeforeRender = () => { if (host.motion()) edge.uniforms.uTime.value = performance.now() / 1000; };
    host.group.add(keyMesh, line);
  }
}

/** Where the enemy portal moves to: a see-through arch on the option's far end, facing back down the new road. */
export function ghostPortal(host: PreviewHost, from: Cell | null, cells: readonly Cell[]): void {
  const L = lib();
  if (!L || !L.hq(HQ_PORTAL.base) || cells.length === 0) return;
  const end = cells[cells.length - 1], prev = cells.length > 1 ? cells[cells.length - 2] : from;
  if (!prev) return;
  const arch = L.clone(`hq:${HQ_PORTAL.base}`);
  host.ghostify(arch);
  arch.rotation.y = yawFor[headingBetween(end, prev)] + HQ_YAW;
  arch.position.copy(cellToWorld(end, roadLevelAt(end.x, end.y)));
  host.group.add(arch);
}

/** A numbered badge over an option's far end, so ROTATE's choices can be told apart, counted and tapped. */
export function numberBadge(host: PreviewHost, at: Cell, n: number, chosen: boolean): void {
  const map = optionBadge(n, chosen);
  if (!map) return;
  const sprite = new THREE.Sprite(badgeMaterial(map));
  sprite.scale.set(OptionBadge.Size, OptionBadge.Size, 1);
  sprite.position.copy(cellToWorld(at, roadLevelAt(at.x, at.y) + (chosen ? OptionBadge.ChosenLift : OptionBadge.Lift)));
  sprite.renderOrder = 12;
  host.group.add(sprite);
}

/** See-through ghosts of the plank bridges a road option will lay over each run of chasm cells. */
export function ghostBridges(host: PreviewHost, path: readonly Cell[], grid: Grid): void {
  const L = lib(), T = Terrain.current;
  if (!L || !T) return;
  const pit = (c: Cell) => grid.kindAt(c) === CellKind.Chasm;
  for (let i = 1; i < path.length; i++) {
    if (!pit(path[i]) || pit(path[i - 1])) continue;
    let j = i;
    while (j + 1 < path.length && pit(path[j + 1])) j++;
    const run = path.slice(i, j + 1), bank = path[i - 1];
    const n = Math.min(3, run.length);
    const name = L.hq(`g_bridge_${n}`) ? `g_bridge_${n}` : L.hq("g_bridge_1") ? "g_bridge_1" : null;
    if (!name) continue;
    const heading = headingBetween(bank, run[0]);
    const deck = L.clone(`hq:${name}`);
    host.ghostify(deck);
    const mid = run[Math.floor(run.length / 2)];
    deck.position.copy(cellToWorld(mid, T.groundOf(bank) + 0.06));
    if (run.length % 2 === 0) { const [dx, dz] = headingOffset(heading); deck.position.x -= dx * 0.5; deck.position.z -= dz * 0.5; }
    deck.rotation.y = yawFor[heading] + HQ_YAW + Math.PI / 2;
    host.group.add(deck);
    i = j;
  }
}

/**
 * A flat band along cell centres at road level (over a chasm, the bank height its bridge would have): a rectangle per step and a disc at every point for round
 * joins. One mesh, one draw; the geometry is the caller's to dispose.
 */
export function ribbon(cells: readonly Cell[], width: number, material: THREE.Material, order: number): THREE.Mesh {
  const pos: number[] = [];
  const h = (x: number, z: number) => roadLevelAt(x, z) + RIBBON_LIFT;
  const tri = (ax: number, az: number, bx: number, bz: number, cx: number, cz: number) => pos.push(ax, h(ax, az), az, bx, h(bx, bz), bz, cx, h(cx, cz), cz);
  const r = width / 2;
  const disc = (x: number, z: number, rad: number) => {
    const n = 16;
    for (let i = 0; i < n; i++) {
      const a0 = (i / n) * Math.PI * 2, a1 = ((i + 1) / n) * Math.PI * 2;
      tri(x, z, x + Math.cos(a1) * rad, z + Math.sin(a1) * rad, x + Math.cos(a0) * rad, z + Math.sin(a0) * rad);
    }
  };
  for (let i = 0; i < cells.length; i++) {
    const c = cells[i];
    disc(c.x, c.y, r);
    if (i === 0) continue;
    const p = cells[i - 1];
    const dx = c.x - p.x, dz = c.y - p.y, len = Math.hypot(dx, dz) || 1;
    const nx = (-dz / len) * r, nz = (dx / len) * r;
    tri(p.x + nx, p.y + nz, c.x + nx, c.y + nz, c.x - nx, c.y - nz);
    tri(p.x + nx, p.y + nz, c.x - nx, c.y - nz, p.x - nx, p.y - nz);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  const m = new THREE.Mesh(g, material);
  m.renderOrder = order;
  return m;
}
